import{useCallback,useEffect,useMemo,useRef,useState}from"react";
import{
  VAULT_ITEM_TYPES,vaultApi,type VaultHistoryEntry,type VaultItem,type VaultItemType,type VaultPayload
}from"../../lib/vaultApi";
import{PageAction,StateText,WorkspaceHeader}from"../../components/Foundation";

type VaultState="locked"|"opening"|"open";
type VaultFilter="ALL"|"LOGIN"|"SUBSCRIPTION"|"SECURE_NOTE"|"WIFI"|"SOFTWARE_LICENSE"|"OTHER";
type EditorState={mode:"create"|"edit";item?:VaultItem}|null;

const TYPE_LABELS:Record<string,string>={
  LOGIN:"Login",
  SUBSCRIPTION:"Subscription",
  WIFI:"Wi-Fi",
  SOFTWARE_LICENSE:"Software license",
  SECURE_NOTE:"Secure note",
  OTHER:"Other",
};

const ALLOWED_FIELDS=[
  "title","url","username","password","notes","category","tags","price","currency",
  "billing_cycle","renewal_date","trial_end","cancellation_deadline","cancellation_url",
  "payment_method_label","license_key","wifi_ssid","totp_secret"
]as const;

const SECRET_FIELDS=new Set(["password","license_key","totp_secret"]);

function s(value:unknown):string{return typeof value==="string"?value:""}
function formatDate(value?:string|null):string{
  if(!value)return"—";
  const d=new Date(value);
  return Number.isNaN(d.getTime())?value:d.toLocaleString();
}
function typeLabel(type?:string):string{return TYPE_LABELS[type??""]??(type??"Item").replaceAll("_"," ").toLowerCase()}
function isPending(item:VaultItem):boolean{return item.status!=="ACTIVE"||Boolean(item.pending_delete_at)}
function searchable(item:VaultItem):string{
  return[
    item.title,item.url,item.username,item.category,item.tags,item.wifi_ssid,typeLabel(item.item_type)
  ].filter(Boolean).join(" ").toLowerCase();
}
function issueCount(security:Record<string,any>):number{
  const counts=security?.counts??{};
  return Number(counts.weak??0)+Number(counts.reused_groups??0)+Number(counts.insecure_urls??0);
}

export function VaultWorkspace({onBack}:{onBack:()=>void}){
  const[state,setState]=useState<VaultState>("locked");
  const[items,setItems]=useState<VaultItem[]>([]);
  const[security,setSecurity]=useState<Record<string,any>>({});
  const[subscriptions,setSubscriptions]=useState<Record<string,any>>({});
  const[query,setQuery]=useState("");
  const[filter,setFilter]=useState<VaultFilter>("ALL");
  const[selectedId,setSelectedId]=useState<string|null>(null);
  const[editor,setEditor]=useState<EditorState>(null);
  const[history,setHistory]=useState<VaultHistoryEntry[]|null>(null);
  const[busy,setBusy]=useState<string|null>(null);
  const[notice,setNotice]=useState<string|null>(null);
  const[revealed,setRevealed]=useState<Record<string,boolean>>({});
  const lockTimer=useRef<number|null>(null);
  const hiddenAt=useRef<number|null>(null);
  const revealTimers=useRef<Record<string,number>>({});

  const clearRevealTimers=useCallback(()=>{
    for(const timer of Object.values(revealTimers.current))window.clearTimeout(timer);
    revealTimers.current={};
  },[]);

  const lockVault=useCallback((message="Vault locked.")=>{
    if(lockTimer.current!==null)window.clearTimeout(lockTimer.current);
    clearRevealTimers();
    setItems([]);
    setSecurity({});
    setSubscriptions({});
    setSelectedId(null);
    setEditor(null);
    setHistory(null);
    setRevealed({});
    setQuery("");
    setFilter("ALL");
    setState("locked");
    setNotice(message);
  },[clearRevealTimers]);

  const armLock=useCallback(()=>{
    if(state!=="open")return;
    if(lockTimer.current!==null)window.clearTimeout(lockTimer.current);
    lockTimer.current=window.setTimeout(
      ()=>lockVault("Vault auto-locked after 5 minutes of inactivity."),
      300000
    );
  },[lockVault,state]);

  useEffect(()=>{
    const activity=()=>armLock();
    document.addEventListener("click",activity,{passive:true});
    document.addEventListener("keydown",activity,{passive:true});
    document.addEventListener("pointerdown",activity,{passive:true});
    return()=>{
      document.removeEventListener("click",activity);
      document.removeEventListener("keydown",activity);
      document.removeEventListener("pointerdown",activity);
    };
  },[armLock]);

  useEffect(()=>{
    const visibility=()=>{
      if(document.hidden){hiddenAt.current=Date.now();return}
      if(hiddenAt.current&&Date.now()-hiddenAt.current>60000&&state==="open"){
        lockVault("Vault auto-locked after being hidden for more than 60 seconds.");
      }
      hiddenAt.current=null;
    };
    document.addEventListener("visibilitychange",visibility);
    return()=>document.removeEventListener("visibilitychange",visibility);
  },[lockVault,state]);

  useEffect(()=>()=>{if(lockTimer.current!==null)window.clearTimeout(lockTimer.current);clearRevealTimers()},[clearRevealTimers]);

  function applyPayload(payload:VaultPayload){
    const next=payload.items??[];
    setItems(next);
    setSecurity(payload.security??{});
    setSubscriptions(payload.subscriptions??{});
    setState("open");
    setSelectedId(next[0]?.id??null);
    setNotice(null);
  }

  async function openVault(){
    setBusy("open");setState("opening");setNotice(null);
    try{
      const payload=await vaultApi.open();
      applyPayload(payload);
    }catch(error){
      setState("locked");
      setNotice(error instanceof Error?error.message:"Unable to unlock the vault.");
    }finally{setBusy(null)}
  }

  function leaveVault(){
    lockVault();
    onBack();
  }

  const filtered=useMemo(()=>{
    const q=query.trim().toLowerCase();
    return items
      .filter(item=>filter==="ALL"||item.item_type===filter)
      .filter(item=>!q||searchable(item).includes(q))
      .sort((a,b)=>{
        if(isPending(a)!==isPending(b))return isPending(a)?1:-1;
        return String(b.updated_at??"").localeCompare(String(a.updated_at??""));
      });
  },[filter,items,query]);

  const selected=useMemo(()=>items.find(item=>item.id===selectedId)??null,[items,selectedId]);
  const subscriptionsVisible=filter==="SUBSCRIPTION"&&state==="open";
  const attention=issueCount(security);

  function reveal(itemId:string,field:string){
    const key=`${itemId}:${field}`;
    setRevealed(current=>({...current,[key]:true}));
    if(revealTimers.current[key])window.clearTimeout(revealTimers.current[key]);
    revealTimers.current[key]=window.setTimeout(()=>{
      setRevealed(current=>{const next={...current};delete next[key];return next});
      delete revealTimers.current[key];
    },15000);
    armLock();
  }

  async function copyValue(value:string,label:string){
    if(!value)return;
    try{
      await navigator.clipboard.writeText(value);
      setNotice(`${label} copied. Clipboard clear is scheduled in 30 seconds.`);
      window.setTimeout(async()=>{try{await navigator.clipboard.writeText("")}catch{}},30000);
      armLock();
    }catch{setNotice(`Could not copy ${label.toLowerCase()}.`)}
  }

  function openUrl(url?:string){
    if(!url)return;
    if(!/^https:\/\//i.test(url)){setNotice("Blocked: only HTTPS links are opened automatically.");return}
    window.open(url,"_blank","noopener,noreferrer");
    armLock();
  }

  function openEditor(next:EditorState){setHistory(null);setEditor(next);armLock()}

  async function saveItem(itemType:VaultItemType,payload:Record<string,unknown>,editing?:VaultItem){
    setBusy("save");setNotice(null);
    try{
      if(editing){
        const result=await vaultApi.update(editing.id,itemType,payload);
        setItems(current=>current.map(item=>item.id===editing.id?{
          ...editing,...payload,item_type:itemType,version:Number(result.version??editing.version+1),
          updated_at:new Date().toISOString()
        }as VaultItem:item));
        setSelectedId(editing.id);
        setNotice("Item updated. Previous versions were preserved.");
      }else{
        const result=await vaultApi.create(itemType,payload);
        const created={
          ...payload,
          id:String(result.id),
          version:Number(result.version??1),
          item_type:itemType,
          status:"ACTIVE",
          pending_delete_at:null,
          updated_at:new Date().toISOString(),
        }as VaultItem;
        setItems(current=>[created,...current]);
        setSelectedId(created.id);
        setNotice("Item saved.");
      }
      setEditor(null);armLock();
    }catch(error){setNotice(error instanceof Error?error.message:"Unable to save item.")}
    finally{setBusy(null)}
  }

  async function loadHistory(item:VaultItem){
    setBusy("history");setNotice(null);
    try{
      const result=await vaultApi.history(item.id);
      setHistory(result);
      setEditor(null);
      armLock();
    }catch(error){setNotice(error instanceof Error?error.message:"Unable to load item history.")}
    finally{setBusy(null)}
  }

  async function restore(item:VaultItem,version:number){
    if(!window.confirm(`Restore version ${version}? The current version remains in history.`))return;
    setBusy("restore");setNotice(null);
    try{
      await vaultApi.restoreVersion(item.id,version);
      lockVault("Version restored. Reopen the vault to view the restored content.");
    }catch(error){setNotice(error instanceof Error?error.message:"Unable to restore version.")}
    finally{setBusy(null)}
  }

  async function scheduleDelete(item:VaultItem){
    if(!window.confirm("Schedule this item for deletion? It remains recoverable during the grace period."))return;
    setBusy("delete");setNotice(null);
    try{
      const result=await vaultApi.scheduleDelete(item.id);
      setItems(current=>current.map(row=>row.id===item.id?{
        ...row,status:"PENDING_DELETE",pending_delete_at:result.delete_after
      }:row));
      setNotice(`Deletion scheduled. The item remains recoverable for ${result.grace_days} days.`);
      armLock();
    }catch(error){setNotice(error instanceof Error?error.message:"Unable to schedule deletion.")}
    finally{setBusy(null)}
  }

  async function cancelDelete(item:VaultItem){
    setBusy("cancel-delete");setNotice(null);
    try{
      await vaultApi.cancelDelete(item.id);
      setItems(current=>current.map(row=>row.id===item.id?{
        ...row,status:"ACTIVE",pending_delete_at:null
      }:row));
      setNotice("Scheduled deletion cancelled.");
      armLock();
    }catch(error){setNotice(error instanceof Error?error.message:"Unable to cancel deletion.")}
    finally{setBusy(null)}
  }

  return <div className="page vault-workspace">
    <button
      className="vault-back"
      onClick={leaveVault}
    >
      ← My Security
    </button>

    <WorkspaceHeader
      eyebrow="Personal Vault"
      title="My Vault"
      description="Passwords, notes and protected information encrypted for your personal security context."
      actions={
        <>
          <StateText
            tone={state==="open"?"positive":"neutral"}
          >
            {state==="open"?"Unlocked":"Locked"}
          </StateText>

          {state==="open"&&
            <button
              className="button secondary compact"
              onClick={()=>lockVault()}
            >
              Lock now
            </button>
          }
        </>
      }
    />

    {notice&&<div className="vault-inline-notice" role="status">{notice}</div>}

    {state!=="open"?<LockedVault state={state} busy={busy} onOpen={()=>void openVault()}/>:<>
      <div className="vault-toolbar">
        <div className="vault-search-wrap">
          <input className="vault-search" value={query} onChange={(e:React.ChangeEvent<HTMLInputElement>)=>setQuery(e.target.value)} placeholder="Search titles, URLs or usernames" aria-label="Search vault"/>
        </div>
        <PageAction
          className="vault-add"
          onClick={()=>openEditor({mode:"create"})}
        >
          + Add item
        </PageAction>
      </div>

      <div className="vault-filters" aria-label="Vault categories">
        {([
          ["ALL","All"],["LOGIN","Logins"],["SUBSCRIPTION","Subscriptions"],["SECURE_NOTE","Secure notes"],
          ["WIFI","Wi-Fi"],["SOFTWARE_LICENSE","Licenses"],["OTHER","Other"]
        ]as const).map(([value,label])=><button key={value} className={filter===value?"selected":""} onClick={()=>{setFilter(value);setSelectedId(null);setEditor(null);setHistory(null)}}>{label}</button>)}
      </div>

      {attention>0&&<div className="vault-attention"><strong>{attention} <span>{attention===1?"vault security signal needs review":"vault security signals need review"}</span></strong><span>Weak, reused or insecure entries are reported from the current decrypted vault view.</span></div>}

      {subscriptionsVisible&&<SubscriptionSummary data={subscriptions}/>}

      <div className="vault-layout">
        <section className="vault-list-panel" aria-label="Vault items">
          <div className="vault-list-meta"><span>{filtered.length} <span>{filtered.length===1?"item":"items"}</span></span><span>Auto-lock · 5 minutes</span></div>
          <div className="vault-list">
            {filtered.length?filtered.map(item=><button key={item.id} className={`vault-item-row ${selectedId===item.id?"selected":""}`} onClick={()=>{setSelectedId(item.id);setEditor(null);setHistory(null);armLock()}}>
              <div className="vault-item-main">
                <strong>{item.title||"Untitled"}</strong>
                <span>{item.url||item.username||item.wifi_ssid||typeLabel(item.item_type)}</span>
              </div>
              <div className="vault-item-side">
                <span>{typeLabel(item.item_type)}</span>
                {isPending(item)&&<em>Deletion scheduled</em>}
              </div>
            </button>):<div className="vault-empty">No matching items.</div>}
          </div>
        </section>

        {(filtered.length>0||editor||history)&&<aside className="vault-detail-panel">
          {editor?<VaultEditor key={`${editor.mode}-${editor.item?.id??"new"}`} state={editor} busy={busy==="save"} onCancel={()=>setEditor(null)} onSave={saveItem}/>:history&&selected?<HistoryPanel item={selected} history={history} busy={busy==="restore"} onClose={()=>setHistory(null)} onRestore={restore}/>:selected?<ItemDetail item={selected} revealed={revealed} busy={busy} onReveal={reveal} onCopy={copyValue} onOpenUrl={openUrl} onEdit={()=>openEditor({mode:"edit",item:selected})} onHistory={()=>void loadHistory(selected)} onDelete={()=>void scheduleDelete(selected)} onCancelDelete={()=>void cancelDelete(selected)}/>:<div className="vault-detail-empty"><strong>Select an item</strong><span>Choose a vault item to view its protected details.</span></div>}
        </aside>}
      </div>
    </>}
  </div>
}

function LockedVault({state,busy,onOpen}:{state:VaultState;busy:string|null;onOpen:()=>void}){
  return <section className="vault-locked">
    <div className="vault-lock-symbol" aria-hidden="true">◇</div>
    <span className="section-kicker">Encrypted and locked</span>
    <h2>{state==="opening"?"Waiting for passkey approval":"Your vault is locked"}</h2>
    <p>Verify with an approved passkey to decrypt your Personal Vault for this browser session.</p>
    <button className="button primary" disabled={busy==="open"} onClick={onOpen}>{state==="opening"?"Opening…":"Unlock with passkey"}</button>
    <small>Automatically locks after 5 minutes of inactivity or extended background time.</small>
  </section>
}

function SubscriptionSummary({data}:{data:Record<string,any>}){
  const monthly=data?.monthly_equivalent??0;
  const yearly=data?.yearly_equivalent??0;
  const upcoming=Array.isArray(data?.upcoming_30_days)?data.upcoming_30_days:[];
  return <div className="subscription-summary">
    <div><span>Monthly equivalent</span><strong>{String(monthly)}</strong></div>
    <div><span>Yearly equivalent</span><strong>{String(yearly)}</strong></div>
    <div><span>Upcoming 30 days</span><strong>{upcoming.length}</strong></div>
  </div>
}

function ItemDetail({
  item,revealed,busy,onReveal,onCopy,onOpenUrl,onEdit,onHistory,onDelete,onCancelDelete
}:{
  item:VaultItem;revealed:Record<string,boolean>;busy:string|null;
  onReveal:(itemId:string,field:string)=>void;
  onCopy:(value:string,label:string)=>Promise<void>;
  onOpenUrl:(url?:string)=>void;
  onEdit:()=>void;onHistory:()=>void;onDelete:()=>void;onCancelDelete:()=>void;
}){
  const secretRows=[
    item.password?["password","Password",item.password]:null,
    item.license_key?["license_key","License key",item.license_key]:null,
    item.totp_secret?["totp_secret","Authenticator secret",item.totp_secret]:null,
  ].filter(Boolean)as[string,string,string][];

  return <div className="vault-detail">
    <div className="vault-detail-head">
      <div><span className="section-kicker">{typeLabel(item.item_type)}</span><h2>{item.title||"Untitled"}</h2></div>
      {isPending(item)&&<span className="vault-pending-badge">Deletion scheduled</span>}
    </div>

    {item.url&&<DetailRow label="Website"><span>{item.url}</span><button onClick={()=>onOpenUrl(item.url)}>Open</button></DetailRow>}
    {item.username&&<DetailRow label="Username"><span>{item.username}</span><button onClick={()=>void onCopy(item.username!,"Username")}>Copy</button></DetailRow>}
    {item.wifi_ssid&&<DetailRow label="Wi-Fi network"><span>{item.wifi_ssid}</span></DetailRow>}

    {secretRows.map(([field,label,value])=>{
      const key=`${item.id}:${field}`;const visible=Boolean(revealed[key]);
      return <DetailRow key={field} label={label}>
        <code className="vault-secret">{visible?value:"••••••••••••"}</code>
        <button onClick={()=>onReveal(item.id,field)}>{visible?"Visible 15s":"Reveal"}</button>
        <button onClick={()=>void onCopy(value,label)}>Copy</button>
      </DetailRow>
    })}

    {item.notes&&<div className="vault-notes"><span>Notes</span><p>{item.notes}</p></div>}
    {item.item_type==="SUBSCRIPTION"&&<SubscriptionDetail item={item}/>}
    {item.category&&<DetailRow label="Category"><span>{item.category}</span></DetailRow>}
    {item.tags&&<DetailRow label="Tags"><span>{item.tags}</span></DetailRow>}

    <div className="vault-version-meta"><span><span>Version</span> {item.version}</span><span><span>Updated</span> {formatDate(item.updated_at)}</span></div>

    {isPending(item)?<div className="vault-delete-panel">
      <p>This item is scheduled for deletion{item.pending_delete_at?` after ${formatDate(item.pending_delete_at)}`:""}.</p>
      <button className="button secondary compact" disabled={busy!==null} onClick={onCancelDelete}>Cancel deletion</button>
    </div>:<div className="vault-detail-actions">
      <button className="button secondary compact" disabled={busy!==null} onClick={onEdit}>Edit</button>
      <button className="button secondary compact" disabled={busy!==null} onClick={onHistory}>History</button>
      <button className="button danger-soft compact" disabled={busy!==null} onClick={onDelete}>Delete</button>
    </div>}
  </div>
}

function DetailRow({label,children}:{label:string;children:React.ReactNode}){
  return <div className="vault-detail-row"><span className="vault-detail-label">{label}</span><div className="vault-detail-value">{children}</div></div>
}

function SubscriptionDetail({item}:{item:VaultItem}){
  const candidateRows:Array<[string,string|undefined]>=[
    ["Price",[item.price,item.currency].filter(Boolean).join(" ")||undefined],
    ["Billing",item.billing_cycle],
    ["Renewal",item.renewal_date],
    ["Trial end",item.trial_end],
    ["Cancellation deadline",item.cancellation_deadline],
    ["Payment",item.payment_method_label],
  ];
  const rows=candidateRows.filter((row):row is [string,string]=>Boolean(row[1]));
  return <div className="vault-subscription-detail">{rows.map(([label,value])=><DetailRow key={label} label={label}><span>{value}</span></DetailRow>)}</div>
}

function HistoryPanel({
  item,history,busy,onClose,onRestore
}:{item:VaultItem;history:VaultHistoryEntry[];busy:boolean;onClose:()=>void;onRestore:(item:VaultItem,version:number)=>void}){
  return <div className="vault-history">
    <div className="vault-detail-head"><div><span className="section-kicker">Version history</span><h2>{item.title||"Untitled"}</h2></div><button className="text-button" onClick={onClose}>Close</button></div>
    <div className="vault-history-list">{history.map(entry=><div className="vault-history-row" key={entry.version}>
      <div><strong>Version {entry.version}</strong><span>{formatDate(entry.created_at)}</span></div>
      {entry.is_current?<span className="semantic-positive">Current</span>:<button className="button secondary compact" disabled={busy} onClick={()=>onRestore(item,entry.version)}>Restore</button>}
    </div>)}</div>
    {!history.length&&<p className="quiet-copy">No previous versions are available.</p>}
  </div>
}

function VaultEditor({
  state,busy,onCancel,onSave
}:{state:Exclude<EditorState,null>;busy:boolean;onCancel:()=>void;onSave:(type:VaultItemType,payload:Record<string,unknown>,editing?:VaultItem)=>Promise<void>}){
  const editing=state.item;
  const[type,setType]=useState<VaultItemType>((editing?.item_type as VaultItemType)||"LOGIN");
  const[field,setField]=useState<Record<string,string>>(()=>{
    const initial:Record<string,string>={};
    for(const key of ALLOWED_FIELDS){
      if(SECRET_FIELDS.has(key))initial[key]="";
      else initial[key]=s(editing?.[key]);
    }
    initial.currency=initial.currency||"EUR";
    initial.billing_cycle=initial.billing_cycle||"monthly";
    return initial;
  });
  const[showPassword,setShowPassword]=useState(false);
  const[generating,setGenerating]=useState(false);

  function set(name:string,value:string){setField(current=>({...current,[name]:value}))}

  async function generate(){
    setGenerating(true);
    try{set("password",await vaultApi.generatePassword(32));setShowPassword(true)}
    finally{setGenerating(false)}
  }

  function buildPayload():Record<string,unknown>{
    const payload:Record<string,unknown>={};
    if(editing){
      for(const key of ALLOWED_FIELDS){
        const value=editing[key];
        if(value!==undefined&&value!==null)payload[key]=String(value);
      }
    }
    for(const key of ALLOWED_FIELDS){
      if(SECRET_FIELDS.has(key)&&editing&&field[key]==="")continue;
      payload[key]=field[key]??"";
    }
    return payload;
  }

  async function submit(event:React.FormEvent){
    event.preventDefault();
    await onSave(type,buildPayload(),editing);
  }

  const hasPassword=type==="LOGIN"||type==="SUBSCRIPTION"||type==="WIFI"||type==="OTHER";
  const hasUrl=type==="LOGIN"||type==="SUBSCRIPTION"||type==="SOFTWARE_LICENSE"||type==="OTHER";
  const hasUsername=type==="LOGIN"||type==="SUBSCRIPTION"||type==="SOFTWARE_LICENSE"||type==="OTHER";

  return <form className="vault-editor" onSubmit={submit}>
    <div className="vault-detail-head"><div><span className="section-kicker">{editing?"Edit item":"New item"}</span><h2>{editing?editing.title||"Untitled":"Add to My Vault"}</h2></div><button type="button" className="text-button" onClick={onCancel}>Cancel</button></div>

    <label>Type
      <select value={type} disabled={Boolean(editing)} onChange={(e:React.ChangeEvent<HTMLSelectElement>)=>setType(e.target.value as VaultItemType)}>
        {VAULT_ITEM_TYPES.map(value=><option key={value} value={value}>{TYPE_LABELS[value]}</option>)}
      </select>
    </label>

    <label>Name<input value={field.title??""} onChange={(e:React.ChangeEvent<HTMLInputElement>)=>set("title",e.target.value)} autoComplete="off"/></label>

    {hasUrl&&<label>Website<input value={field.url??""} onChange={(e:React.ChangeEvent<HTMLInputElement>)=>set("url",e.target.value)} placeholder="https://example.com" autoComplete="off"/></label>}
    {hasUsername&&<label>Username or email<input value={field.username??""} onChange={(e:React.ChangeEvent<HTMLInputElement>)=>set("username",e.target.value)} autoComplete="off"/></label>}
    {type==="WIFI"&&<label>Wi-Fi network<input value={field.wifi_ssid??""} onChange={(e:React.ChangeEvent<HTMLInputElement>)=>set("wifi_ssid",e.target.value)} autoComplete="off"/></label>}

    {hasPassword&&<label>Password
      <div className="vault-secret-input">
        <input type={showPassword?"text":"password"} value={field.password??""} onChange={(e:React.ChangeEvent<HTMLInputElement>)=>set("password",e.target.value)} placeholder={editing&&editing.password?"Leave blank to keep current password":""} autoComplete="new-password"/>
        <button type="button" className="text-button" onClick={()=>setShowPassword(v=>!v)}>{showPassword?"Hide":"Show"}</button>
        <button type="button" className="text-button" disabled={generating} onClick={()=>void generate()}>{generating?"Generating…":"Generate"}</button>
      </div>
    </label>}

    {type==="LOGIN"&&<label>Authenticator secret<input type="password" value={field.totp_secret??""} onChange={(e:React.ChangeEvent<HTMLInputElement>)=>set("totp_secret",e.target.value)} placeholder={editing&&editing.totp_secret?"Leave blank to keep current secret":""} autoComplete="off"/></label>}
    {type==="SOFTWARE_LICENSE"&&<label>License key<input type="password" value={field.license_key??""} onChange={(e:React.ChangeEvent<HTMLInputElement>)=>set("license_key",e.target.value)} placeholder={editing&&editing.license_key?"Leave blank to keep current key":""} autoComplete="off"/></label>}

    {type==="SUBSCRIPTION"&&<div className="vault-form-grid">
      <label>Price<input value={field.price??""} onChange={(e:React.ChangeEvent<HTMLInputElement>)=>set("price",e.target.value)} inputMode="decimal"/></label>
      <label>Currency<input value={field.currency??"EUR"} onChange={(e:React.ChangeEvent<HTMLInputElement>)=>set("currency",e.target.value)}/></label>
      <label>Billing cycle<select value={field.billing_cycle??"monthly"} onChange={(e:React.ChangeEvent<HTMLSelectElement>)=>set("billing_cycle",e.target.value)}><option value="weekly">Weekly</option><option value="monthly">Monthly</option><option value="quarterly">Quarterly</option><option value="yearly">Yearly</option></select></label>
      <label>Renewal date<input type="date" value={field.renewal_date??""} onChange={(e:React.ChangeEvent<HTMLInputElement>)=>set("renewal_date",e.target.value)}/></label>
      <label>Trial end<input type="date" value={field.trial_end??""} onChange={(e:React.ChangeEvent<HTMLInputElement>)=>set("trial_end",e.target.value)}/></label>
      <label>Cancellation deadline<input type="date" value={field.cancellation_deadline??""} onChange={(e:React.ChangeEvent<HTMLInputElement>)=>set("cancellation_deadline",e.target.value)}/></label>
      <label className="span-two">Cancellation URL<input value={field.cancellation_url??""} onChange={(e:React.ChangeEvent<HTMLInputElement>)=>set("cancellation_url",e.target.value)} placeholder="https://"/></label>
      <label className="span-two">Payment method label<input value={field.payment_method_label??""} onChange={(e:React.ChangeEvent<HTMLInputElement>)=>set("payment_method_label",e.target.value)} placeholder="Label only — never a full card number"/></label>
    </div>}

    {type==="OTHER"&&<div className="vault-form-grid">
      <label>Category<input value={field.category??""} onChange={(e:React.ChangeEvent<HTMLInputElement>)=>set("category",e.target.value)}/></label>
      <label>Tags<input value={field.tags??""} onChange={(e:React.ChangeEvent<HTMLInputElement>)=>set("tags",e.target.value)}/></label>
    </div>}

    <label>Notes<textarea value={field.notes??""} onChange={(e:React.ChangeEvent<HTMLTextAreaElement>)=>set("notes",e.target.value)} rows={5}/></label>

    <div className="vault-editor-footer">
      <p>A fresh passkey approval is required to {editing?"save changes":"save this item"}.</p>
      <button className="button primary" disabled={busy} type="submit">{busy?"Waiting for approval…":editing?"Save changes":"Save item"}</button>
    </div>
  </form>
}
